import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Site-wide age verification gate. A visitor must press one of the dialog's own buttons to
 * move past it: unlike every other dialog in this theme, there is no close button, no
 * outside-click dismissal, and the native `cancel` event (Escape) is suppressed on both of
 * its panels. This is a legal requirement, not an interruption to make easy to skip.
 *
 * Declining swaps the dialog's content from the question panel to a "sorry" panel in place,
 * rather than closing the dialog and opening a second one — the page behind it is never
 * briefly unlocked between the two. The sorry panel's one button swaps the question back in
 * (`D-279`), so a misclick costs one press rather than a reload; a "no" is never stored.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} questionPanel
 * @property {HTMLElement} questionHeading
 * @property {HTMLElement} sorryPanel
 * @property {HTMLElement} sorryHeading
 *
 * @extends Component<Refs>
 */
export class AgeGateDialog extends Component {
  requiredRefs = ['dialog', 'questionPanel', 'questionHeading', 'sorryPanel', 'sorryHeading'];

  static #STORAGE_KEY = 'ageVerified';

  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    if (AgeGateDialog.#isVerified()) return;

    this.refs.dialog.addEventListener('cancel', (event) => event.preventDefault());
    this.#open();
  }

  confirmAge() {
    try {
      localStorage.setItem(AgeGateDialog.#STORAGE_KEY, '1');
    } catch {
      // Storage can be unavailable (private browsing, disabled storage). The visitor has
      // still answered for this page view, which is what the close below is for.
    }
    this.#close();
  }

  declineAge() {
    const { dialog, questionPanel, sorryPanel, sorryHeading } = this.refs;

    questionPanel.hidden = true;
    sorryPanel.hidden = false;
    dialog.setAttribute('aria-labelledby', 'age-gate-sorry-heading');
    dialog.setAttribute('aria-describedby', 'age-gate-sorry-description');

    // The dialog's accessible name just changed under a screen reader that already
    // announced the question panel; move focus to the new heading so that update is read.
    sorryHeading.focus();
  }

  /* The way back from the sorry panel (`D-279`). It replaced "Leave this site", which sent the
     visitor to google.com: a dead end for someone under 21, and for someone who pressed the
     wrong button a trap, since only a reload brought the question back. Nothing was stored on
     decline, so going back is the exact reverse of `declineAge` — panels, the dialog's
     accessible name, and focus on the question's heading so a screen reader reads it again. */
  returnToQuestion() {
    const { dialog, questionPanel, questionHeading, sorryPanel } = this.refs;

    sorryPanel.hidden = true;
    questionPanel.hidden = false;
    dialog.setAttribute('aria-labelledby', 'age-gate-heading');
    dialog.setAttribute('aria-describedby', 'age-gate-description');

    questionHeading.focus();
  }

  static #isVerified() {
    try {
      return localStorage.getItem(AgeGateDialog.#STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  #open() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      dialog.showModal();
      // `showModal()` focuses the dialog's first focusable descendant, and since `D-279` gave
      // the question heading `tabindex="-1"` (so `returnToQuestion` can focus it) that is the
      // heading: every open drew the shared focus ring around the title. Focus the dialog
      // itself instead — its accessible name still announces the question, its outline is
      // suppressed in the snippet's stylesheet, and the first Tab lands on the confirm button.
      // Same task as `showModal()`, so nothing paints with the heading focused.
      dialog.focus();
    });
  }

  #close = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    // Force the browser to restart the closing animation by resetting it first.
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    await onAnimationEnd(dialog, undefined, { subtree: false });

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });

    dialog.close();
    dialog.classList.remove('dialog-closing');
  };
}

if (!customElements.get('age-gate-dialog')) customElements.define('age-gate-dialog', AgeGateDialog);
